import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Settings, Copy, Eye, EyeOff, Loader2, CreditCard, Search, MessageCircle, Gauge, DollarSign } from "lucide-react";
import { invalidateAppSettings } from "@/hooks/useAppSettings";

export default function AdminConfiguracoes() {
  const publicKey = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY || "";
  const [showPublicKey, setShowPublicKey] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [saving, setSaving] = useState(false);

  const [firecrawlKey, setFirecrawlKey] = useState("");
  const [savingFirecrawl, setSavingFirecrawl] = useState(false);

  // Settings públicas
  const [freeLimit, setFreeLimit] = useState("3");
  const [whatsNumber, setWhatsNumber] = useState("");
  const [whatsMessage, setWhatsMessage] = useState("");
  const [proPrice, setProPrice] = useState("49.00");
  const [savingSetting, setSavingSetting] = useState<string | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", [
          "FREE_SIMULATION_LIMIT",
          "WHATSAPP_NUMBER",
          "WHATSAPP_MESSAGE",
          "PRO_PLAN_PRICE",
        ]);
      const map = Object.fromEntries((data || []).map((r: any) => [r.key, r.value]));
      setFreeLimit(map.FREE_SIMULATION_LIMIT || "3");
      setWhatsNumber(map.WHATSAPP_NUMBER || "");
      setWhatsMessage(map.WHATSAPP_MESSAGE || "");
      setProPrice(map.PRO_PLAN_PRICE || "49.00");
      setLoadingSettings(false);
    })();
  }, []);

  async function saveSetting(key: string, value: string) {
    setSavingSetting(key);
    try {
      const res = await supabase.functions.invoke("admin-update-secret", {
        body: { secret_name: key, secret_value: value },
      });
      if (res.error) throw res.error;
      invalidateAppSettings();
      toast.success("Configuração salva!");
    } catch (err: any) {
      toast.error(err.message || "Erro ao salvar");
    } finally {
      setSavingSetting(null);
    }
  }

  const masked = (val: string) => val ? val.slice(0, 12) + "••••••••" + val.slice(-4) : "Não configurada";

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const handleUpdateAccessToken = async () => {
    if (!accessToken.trim()) {
      toast.error("Informe o Access Token.");
      return;
    }
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Não autenticado");

      const res = await supabase.functions.invoke("admin-update-secret", {
        body: { secret_name: "MERCADOPAGO_ACCESS_TOKEN", secret_value: accessToken.trim() },
      });

      if (res.error) throw res.error;
      toast.success("Access Token atualizado com sucesso!");
      setAccessToken("");
    } catch (err: any) {
      toast.error(err.message || "Erro ao atualizar secret.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFirecrawlKey = async () => {
    if (!firecrawlKey.trim()) {
      toast.error("Informe a API Key do Firecrawl.");
      return;
    }
    setSavingFirecrawl(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Não autenticado");

      const res = await supabase.functions.invoke("admin-update-secret", {
        body: { secret_name: "FIRECRAWL_API_KEY", secret_value: firecrawlKey.trim() },
      });

      if (res.error) throw res.error;
      toast.success("Firecrawl API Key atualizada com sucesso!");
      setFirecrawlKey("");
    } catch (err: any) {
      toast.error(err.message || "Erro ao atualizar secret.");
    } finally {
      setSavingFirecrawl(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" /> Configurações
        </h1>
        <p className="text-muted-foreground">Gerencie as credenciais e integrações do sistema.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" /> Mercado Pago
          </CardTitle>
          <CardDescription>Credenciais para processamento de pagamentos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Public Key */}
          <div className="space-y-2">
            <Label>Public Key (Frontend)</Label>
            <div className="flex gap-2">
              <Input
                readOnly
                value={showPublicKey ? publicKey : masked(publicKey)}
                className="font-mono text-sm"
              />
              <Button variant="outline" size="icon" onClick={() => setShowPublicKey(!showPublicKey)}>
                {showPublicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="icon" onClick={() => copyToClipboard(publicKey)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Variável <code>VITE_MERCADOPAGO_PUBLIC_KEY</code> — usada no SDK do frontend.
            </p>
          </div>

          {/* Access Token */}
          <div className="space-y-2">
            <Label>Access Token (Backend)</Label>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="Cole o novo Access Token aqui..."
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                className="font-mono text-sm"
              />
              <Button onClick={handleUpdateAccessToken} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Atualizar"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Secret <code>MERCADOPAGO_ACCESS_TOKEN</code> — armazenado com segurança no Supabase Vault.
              Por segurança, o valor atual não é exibido.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" /> Firecrawl (Busca OLX)
          </CardTitle>
          <CardDescription>API Key para busca de preços de mercado via Firecrawl.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>API Key</Label>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="Cole a API Key do Firecrawl aqui..."
              value={firecrawlKey}
              onChange={(e) => setFirecrawlKey(e.target.value)}
              className="font-mono text-sm"
            />
            <Button onClick={handleUpdateFirecrawlKey} disabled={savingFirecrawl}>
              {savingFirecrawl ? <Loader2 className="h-4 w-4 animate-spin" /> : "Atualizar"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Secret <code>FIRECRAWL_API_KEY</code> — usada na Edge Function para pesquisar anúncios OLX e preços de peças.
            Obtenha em <a href="https://firecrawl.dev" target="_blank" rel="noopener noreferrer" className="text-primary underline">firecrawl.dev</a>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5" /> Limite de Simulações Grátis
          </CardTitle>
          <CardDescription>
            Quantas simulações cada usuário/visitante pode realizar por mês antes de precisar do plano PRO.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>Simulações por mês</Label>
          <div className="flex gap-2 max-w-xs">
            <Input
              type="number"
              min={0}
              value={freeLimit}
              onChange={(e) => setFreeLimit(e.target.value)}
              disabled={loadingSettings}
            />
            <Button
              onClick={() => saveSetting("FREE_SIMULATION_LIMIT", freeLimit)}
              disabled={savingSetting === "FREE_SIMULATION_LIMIT"}
            >
              {savingSetting === "FREE_SIMULATION_LIMIT"
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : "Salvar"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Usuários PRO ativos têm simulações ilimitadas independentemente deste valor.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" /> WhatsApp
          </CardTitle>
          <CardDescription>
            Número usado pelo botão flutuante e pelo CTA "Falar com Vendas" da página de preços.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Número (DDI + DDD + número, só dígitos)</Label>
            <div className="flex gap-2">
              <Input
                placeholder="5511999999999"
                value={whatsNumber}
                onChange={(e) => setWhatsNumber(e.target.value.replace(/\D/g, ""))}
                disabled={loadingSettings}
                className="font-mono"
              />
              <Button
                onClick={() => saveSetting("WHATSAPP_NUMBER", whatsNumber)}
                disabled={savingSetting === "WHATSAPP_NUMBER"}
              >
                {savingSetting === "WHATSAPP_NUMBER"
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : "Salvar"}
              </Button>
            </div>
          </div>
          <div>
            <Label>Mensagem padrão</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Olá! Tenho interesse na LEILIZE."
                value={whatsMessage}
                onChange={(e) => setWhatsMessage(e.target.value)}
                disabled={loadingSettings}
              />
              <Button
                onClick={() => saveSetting("WHATSAPP_MESSAGE", whatsMessage)}
                disabled={savingSetting === "WHATSAPP_MESSAGE"}
              >
                {savingSetting === "WHATSAPP_MESSAGE"
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : "Salvar"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Plano PRO
          </CardTitle>
          <CardDescription>
            Preço mensal cobrado no checkout do plano PRO.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>Preço mensal (R$)</Label>
          <div className="flex gap-2 max-w-xs">
            <Input
              type="number"
              step="0.01"
              min={0}
              value={proPrice}
              onChange={(e) => setProPrice(e.target.value)}
              disabled={loadingSettings}
            />
            <Button
              onClick={() => saveSetting("PRO_PLAN_PRICE", proPrice)}
              disabled={savingSetting === "PRO_PLAN_PRICE"}
            >
              {savingSetting === "PRO_PLAN_PRICE"
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : "Salvar"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Alterar afeta apenas novos checkouts. Assinaturas em curso no Mercado Pago mantêm o valor original.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

